import axios from 'axios';
import { TOKEN_KEY } from './auth';

// Set proper default values for API endpoints
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PRODUCTS_ENDPOINT = process.env.REACT_APP_PRODUCTS_ENDPOINT || '/products';

// Debug configuration
console.log('Product API Configuration:');
console.log('API URL:', API_URL);
console.log('PRODUCTS ENDPOINT:', PRODUCTS_ENDPOINT);
console.log('TOKEN KEY:', TOKEN_KEY);

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000 // 10 second timeout
});

// Add request interceptor to attach the token to all requests
api.interceptors.request.use(config => {
  console.log('API Request:', config.method, config.url);
  
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    // Set the Authorization header with 'Bearer ' prefix
    config.headers.Authorization = `Bearer ${token}`;
    console.log('Token attached to request');
  } else {
    console.log('No token found in localStorage');
  }
  
  return config;
}, error => {
  console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

// Add response interceptor for debugging
api.interceptors.response.use(response => {
  console.log('API Response:', response.status, response.config.url);
  return response;
}, error => {
  console.error('API Error:', error.response?.status, error.response?.data, error.config?.url);
  return Promise.reject(error);
});

// Get all products
export const getAllProducts = async () => {
  console.log('Calling getAllProducts()');
  try {
    console.log(`Making request to: ${API_URL}${PRODUCTS_ENDPOINT}`);
    const startTime = Date.now();
    const response = await api.get(`${PRODUCTS_ENDPOINT}`);
    const timeElapsed = Date.now() - startTime;
    console.log(`Products fetched in ${timeElapsed}ms:`, response.data.length);
    
    // Check if response data is valid
    if (!Array.isArray(response.data)) {
      console.error('Invalid response format - expected array but got:', typeof response.data);
      return [];
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    if (error.code === 'ECONNABORTED') {
      console.error('Request timed out - server might be down or overloaded');
    } else if (!error.response) {
      console.error('Network error - check server connectivity');
    }
    throw error;
  }
};

// Get a single product by ID
export const getProductById = async (productId) => {
  console.log(`Calling getProductById(${productId})`);
  try {
    const response = await api.get(`${PRODUCTS_ENDPOINT}/${productId}`);
    console.log('Product fetched:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching product ${productId}:`, error);
    throw error;
  }
};

// Create a new product
export const createProduct = async (productData) => {
  console.log('Calling createProduct() with data:', productData);
  try {
    // Check if user is authenticated before making request
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }
    
    const response = await api.post(`${PRODUCTS_ENDPOINT}`, productData);
    console.log('Product created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

// Update an existing product
export const updateProduct = async (productId, productData) => {
  console.log(`Calling updateProduct(${productId}) with data:`, productData);
  try {
    // Check if user is authenticated before making request
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }
    
    const response = await api.put(`${PRODUCTS_ENDPOINT}/${productId}`, productData);
    console.log('Product updated:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating product ${productId}:`, error);
    throw error;
  }
};

// Delete a product
export const deleteProduct = async (productId) => {
  console.log(`Calling deleteProduct(${productId})`);
  try {
    // Check if user is authenticated before making request
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }
    
    const response = await api.delete(`${PRODUCTS_ENDPOINT}/${productId}`);
    console.log('Product deleted:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error deleting product ${productId}:`, error);
    throw error;
  }
};

// Get low stock products
export const getLowStockProducts = async () => {
  console.log('Calling getLowStockProducts()');
  try {
    const response = await api.get(`${PRODUCTS_ENDPOINT}/low-stock`);
    console.log('Low stock products fetched:', response.data.length);
    return response.data;
  } catch (error) {
    console.error('Error fetching low stock products:', error);
    throw error;
  }
}; 